import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'; // Removed toSignal
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { FeedbackDialogComponent } from './feedback-dialog/feedback-dialog.component';
import { FeedbackFacadeService } from '../application/feedback-facade.service';
import { AuthService } from '../../../core/services/auth.service';
import { LayoutService } from '../../layout/service/layout.service';

@Component({
  selector: 'app-feedback-host',
  standalone: true,
  imports: [CommonModule, TranslateModule, FeedbackDialogComponent],
  templateUrl: './feedback-host.component.html',
  styleUrl: './feedback-host.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackHostComponent implements OnInit, OnDestroy {
  readonly feedbackFacade = inject(FeedbackFacadeService);
  private readonly authService = inject(AuthService);
  private readonly layoutService = inject(LayoutService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly currentUrl = signal(this.router.url || window.location.pathname + window.location.search + window.location.hash);
  readonly isRestrictedMode = signal(false);

  // ✅ Use the existing public method directly. 
  // It will re-evaluate whenever currentUrl changes (e.g., after login navigation).
  readonly showLauncher = computed(() => {
    const loggedIn = this.authService.isLoggedIn();
    const url = this.currentUrl();
    const isAuthPage = url.includes('/auth') || url.includes('/login');

    // Fail fast: If not logged in OR on an auth page, hide immediately
    if (!loggedIn || isAuthPage) {
      return false;
    }

    // Otherwise, defer to your existing facade logic
    return this.feedbackFacade.canShowLauncher(url, this.isRestrictedMode());
  });

  ngOnInit(): void {
    if (this.authService.isLoggedIn()) {
      this.feedbackFacade.startDiagnosticsCapture();
    }

    this.layoutService.isRestrictedMode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isRestricted) => {
      this.isRestrictedMode.set(isRestricted);
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.currentUrl.set(this.router.url || window.location.pathname + window.location.search + window.location.hash);
      });
  }

  ngOnDestroy(): void {
    this.feedbackFacade.close();
    this.feedbackFacade.stopDiagnosticsCapture();
    this.feedbackFacade.clearDiagnosticsCapture();
  }

  openFeedback(): void {
    if (!this.showLauncher()) {
      return;
    }

    this.feedbackFacade.open();
  }
}